import type {
  RepositoryIntelligenceLinkList,
  RepositoryIntelligenceRegistrationReceipt,
  RepositoryIntelligenceRemoval,
  RepositoryIntelligenceSettings,
  RepositoryIntelligenceSyncList,
  RepositoryIntelligenceSyncReceipt,
  RepositoryLinkSummary,
  UpdateRepositoryIntelligenceSettingsRequest,
} from '@ashniva/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiRequest } from '../../shared/lib/api-client';
import { classifyProblem } from './ri-problems';
import type { PollInterval } from './use-poll-window';

/**
 * Desk's Repository Intelligence endpoints: status, registration, syncs and the organization's settings. The
 * browser only ever talks to Desk; how Desk reaches Repository Intelligence is the server's business.
 */
export const riKeys = {
  all: ['repository-intelligence'] as const,
  settings: ['repository-intelligence', 'settings'] as const,
  links: ['repository-intelligence', 'links'] as const,
  projectLinks: (projectId: string) => ['repository-intelligence', 'project', projectId] as const,
  link: (linkId: string) => ['repository-intelligence', 'link', linkId] as const,
  status: (linkId: string) => ['repository-intelligence', 'link', linkId, 'status'] as const,
  registration: (linkId: string) =>
    ['repository-intelligence', 'link', linkId, 'registration'] as const,
  syncs: (linkId: string) => ['repository-intelligence', 'link', linkId, 'syncs'] as const,
};

export const intelligencePath = (linkId: string) =>
  `/repositories/${encodeURIComponent(linkId)}/intelligence`;

/**
 * Asking again is only worth it when the problem may pass by itself. A refusal, a 404 or a 409 will say the same
 * thing a second later, and every request is real work on the server and an audit row.
 */
export function riRetry(failureCount: number, error: unknown): boolean {
  return failureCount < 1 && classifyProblem(error) === 'unavailable';
}

/**
 * A key for one user action, so that a retried request returns what the first one made instead of doing the work
 * twice (API-CONTRACTS §2.6). The server takes 16–64 of `A-Z a-z 0-9 _ -`: a UUID's 32 hex digits.
 */
export function newClientRequestId(): string {
  return crypto.randomUUID().replace(/-/g, '');
}

/**
 * Sending the same submission again is safe — it carries the same key, so the server returns what the first one made —
 * and worth it once when Desk could not be reached.
 */
export function riMutationRetry(failureCount: number, error: unknown): boolean {
  return riRetry(failureCount, error);
}

/** What a submission that starts work on the server carries: its idempotency key (see `useSubmissionKey`). */
export interface Submission {
  clientRequestId: string;
}

export function useRiSettingsQuery(enabled = true) {
  return useQuery({
    queryKey: riKeys.settings,
    queryFn: () => apiRequest<RepositoryIntelligenceSettings>('/repository-intelligence/settings'),
    enabled,
    retry: riRetry,
  });
}

export function useSaveRiSettings() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      values: UpdateRepositoryIntelligenceSettingsRequest;
      headers: Record<string, string>;
    }) =>
      apiRequest<RepositoryIntelligenceSettings>('/repository-intelligence/settings', {
        method: 'PUT',
        body: input.values,
        headers: input.headers,
      }),
    // Every screen's answer may change with a switch.
    onSuccess: () => client.invalidateQueries({ queryKey: riKeys.all }),
  });
}

export function useRiLinksQuery(enabled = true) {
  return useQuery({
    queryKey: riKeys.links,
    queryFn: () =>
      apiRequest<RepositoryIntelligenceLinkList>('/repository-intelligence/repositories'),
    enabled,
    retry: riRetry,
  });
}

/** The project's linked repositories (`repository:read`): the Code tab's list and the workspace's header. */
export function useProjectLinksQuery(projectId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: riKeys.projectLinks(projectId ?? ''),
    queryFn: () =>
      apiRequest<RepositoryLinkSummary[]>(
        `/projects/${encodeURIComponent(projectId ?? '')}/repositories`,
      ),
    enabled: enabled && Boolean(projectId),
  });
}

export function useRequestRegistration(linkId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ headers, clientRequestId }: Submission & { headers: Record<string, string> }) =>
      apiRequest<RepositoryIntelligenceRegistrationReceipt>(
        `${intelligencePath(linkId)}/registration`,
        { method: 'POST', body: { clientRequestId }, headers },
      ),
    retry: riMutationRetry,
    // Also after "already registered": the server kept that registration's id, so following it now works.
    onSettled: () => client.invalidateQueries({ queryKey: riKeys.link(linkId) }),
  });
}

export function useRemoveRegistration(linkId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ headers, clientRequestId }: Submission & { headers: Record<string, string> }) =>
      apiRequest<RepositoryIntelligenceRemoval>(`${intelligencePath(linkId)}/registration`, {
        method: 'DELETE',
        body: { clientRequestId },
        headers,
      }),
    retry: riMutationRetry,
    onSuccess: () => client.invalidateQueries({ queryKey: riKeys.link(linkId) }),
  });
}

export function useRiSyncsQuery(
  linkId: string,
  enabled: boolean,
  refetchInterval?: PollInterval<RepositoryIntelligenceSyncList>,
) {
  return useQuery({
    queryKey: riKeys.syncs(linkId),
    queryFn: () =>
      apiRequest<RepositoryIntelligenceSyncList>(`${intelligencePath(linkId)}/syncs`, {
        query: { limit: 20 },
      }),
    enabled,
    retry: riRetry,
    refetchInterval,
  });
}

export function useRequestSync(linkId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ clientRequestId }: Submission) =>
      apiRequest<RepositoryIntelligenceSyncReceipt>(`${intelligencePath(linkId)}/sync`, {
        method: 'POST',
        body: { clientRequestId },
      }),
    retry: riMutationRetry,
    onSuccess: () => client.invalidateQueries({ queryKey: riKeys.syncs(linkId) }),
  });
}
